import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, FolderOpen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProjectCard } from '@/components/projects/ProjectCard';
import { ProjectCreateDialog } from '@/components/projects/ProjectCreateDialog';
import { useStore } from '@/store/useStore';
import api, { projectsApi, handleApiError } from '@/services/api';
import { toast } from 'sonner';

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projects, setProjects } = useStore();
  const [loading, setLoading] = useState(true);
  const [searchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await projectsApi.list();
      setProjects(data);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    if (!window.confirm(t('dashboard.deleteConfirm'))) {
      return;
    }
    
    try {
      await projectsApi.delete(projectId);
      toast.success(t('dashboard.projectDeleted'));
      loadProjects();
    } catch (error) {
      toast.error(handleApiError(error));
    }
  };

  const filteredProjects = projects.filter((p) =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const [llmStats, setLlmStats] = useState({ calls_today: 0, calls_this_month: 0 });

  useEffect(() => {
    loadLlmStats();
  }, []);

  const loadLlmStats = async () => {
    try {
      const response = await api.get('/stats/llm-usage');
      setLlmStats(response.data);
    } catch (error) {
      console.error('Failed to load LLM stats:', error);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Welcome Section */}
      <div>
        <h1 className="text-3xl font-bold mb-2">{t('dashboard.welcomeBack')}</h1>
        <p className="text-muted-foreground">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* LLM Usage Stats */}
      <div className="flex flex-wrap gap-4">
        <Card className="w-48">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {t('dashboard.llmCallsToday')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold">{llmStats.calls_today}</div>
          </CardContent>
        </Card>

        <Card className="w-48">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              {t('dashboard.llmCallsMonth')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold">{llmStats.calls_this_month}</div>
          </CardContent>
        </Card>
      </div>

      {/* Header with Create Button */}
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">{t('dashboard.yourProjects')}</h2>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('dashboard.newProject')}
        </Button>
      </div>

      {/* Projects List */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : filteredProjects.length === 0 && searchQuery ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('dashboard.noProjectsFound')}</h3>
              <p className="text-muted-foreground text-center max-w-md">
                {t('dashboard.tryAdjusting')}
              </p>
            </CardContent>
          </Card>
        ) : projects.length === 0 ? (
          <Card className="border-2 border-dashed border-primary/30">
            <CardContent className="py-12 text-center space-y-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">{t('dashboard.welcomeTitle')}</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('dashboard.welcomeDescription')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
                <div className="flex gap-3 items-start p-4 rounded-lg bg-muted/50">
                  <div className="rounded-full bg-primary/10 p-2 shrink-0"><span className="text-sm font-bold text-primary">1</span></div>
                  <div>
                    <p className="font-medium text-sm">{t('dashboard.step1Title')}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.step1Desc')}</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start p-4 rounded-lg bg-muted/50">
                  <div className="rounded-full bg-primary/10 p-2 shrink-0"><span className="text-sm font-bold text-primary">2</span></div>
                  <div>
                    <p className="font-medium text-sm">{t('dashboard.step2Title')}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.step2Desc')}</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start p-4 rounded-lg bg-muted/50">
                  <div className="rounded-full bg-primary/10 p-2 shrink-0"><span className="text-sm font-bold text-primary">3</span></div>
                  <div>
                    <p className="font-medium text-sm">{t('dashboard.step3Title')}</p>
                    <p className="text-xs text-muted-foreground">{t('dashboard.step3Desc')}</p>
                  </div>
                </div>
              </div>
              <Button onClick={() => setCreateDialogOpen(true)} size="lg" className="mt-4">
                <Plus className="h-5 w-5 mr-2" />
                {t('dashboard.createFirst')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={() => navigate(`/projects/${project.id}`)}
                onDelete={handleDeleteProject}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Project Dialog */}
      <ProjectCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          loadProjects();
          setCreateDialogOpen(false);
        }}
      />
    </div>
  );
}
